/** Audit Center wire contract. Status and deadline bands are server-owned. */
import axios from 'axios'
import { api } from './api'
import { auditCenterMockTransport } from './audit-center.mock'
import { isPortalIdentityMode } from './auth-mode'

export type AuditEventStatus = 'open' | 'awaiting_client' | 'ready_to_submit' | 'submitted' | 'awaiting_determination' | 'closed'
export type AuditKind = 'new_entrant' | 'compliance_review' | 'rating_upgrade' | 'mock_audit'
export interface AuditIntakePayload {
  company_name: string
  dot_number?: string
  contact_name: string
  email: string
  phone?: string
  product: 'audit' | 'smp'
  data_notice_ack: boolean
  letter: File
}
export type AuditWireItemStatus = 'satisfied' | 'open' | 'needs_fix' | 'checking' | 'unavailable' | 'blocked_by_switch'
export type AuditDerivedItemStatus = 'generated' | 'signed' | 'answered' | 'resolved'
export type AuditItemStatus = AuditWireItemStatus | AuditDerivedItemStatus | 'waived'
export type AuditItemKind = 'upload' | 'document' | 'file' | 'answer' | 'artifact' | 'ad_hoc'
/** The backend has not frozen the due-band vocabulary; preserve its value. */
export type DueBand = string
/** One property of a catalog card's `answer_schema` (the JSON Schema subset the catalog uses). */
export interface AuditAnswerField {
  type: 'string' | 'number' | 'integer' | 'boolean'
  title?: string
  format?: string
  pattern?: string
  enum?: string[]
  minimum?: number
  maximum?: number
  maxLength?: number
}
/** The catalog card's JSON Schema verbatim; `answer_item` validates the posted object against it. */
export interface AuditAnswerSchema { type: 'object'; required?: string[]; properties: Record<string, AuditAnswerField> }

/** engine.Counts verbatim. `applicable` is the denominator; blocked rows never count as satisfied. */
export interface AuditCounts {
  applicable: number
  satisfied: number
  waived: number
  blocked: number
  not_applicable: number
  ready_to_submit: boolean
}
/** The catalog's topic keys (action_center/catalog.py `Topic`); the wire keeps the key. */
export type AuditTopic = 'company' | 'drivers' | 'vehicles' | 'drug_alcohol' | 'logs' | 'crashes' | 'hazmat'
const topicLabels: Record<AuditTopic, string> = {
  company: 'Company',
  drivers: 'Drivers',
  vehicles: 'Vehicles',
  drug_alcohol: 'Drug and alcohol',
  logs: 'Hours of service',
  crashes: 'Crashes',
  hazmat: 'Hazmat',
}
/** A topic key in plain words; an unknown key reads as "Other", never as the raw key. */
export function auditTopicLabel(topic: string | null | undefined): string {
  return topicLabels[topic as AuditTopic] ?? 'Other'
}
export interface ClientItem {
  id: string
  item_key: string
  card_code?: string | null
  kind: AuditItemKind
  title: string
  ad_hoc_title?: string | null
  why?: string | null
  topic?: string | null
  applicable: boolean
  scope?: 'client' | 'driver' | 'vehicle'
  waived?: boolean
  status: AuditItemStatus
  reason: string | null
  status_source?: string | null
  max_files?: number | null
  files_received?: number
  answer_schema?: AuditAnswerSchema | null
  answer?: Record<string, unknown> | null
  /** `driving_record`: a CDL driver's medical row, proven only by the state driving record; uploads are refused. */
  proof?: 'driving_record' | null
}
export interface OpsItem extends ClientItem {
  subject_key?: string | null
  waived_at?: string | null
  resolved_at?: string | null
  evidence_ids?: string[]
}
export type AuditItemScope = 'client' | 'driver' | 'vehicle'
/** POST /ops/audits/events/{id}/items (ItemBody, extra=forbid). `document` is an ad-hoc request with
 * no card (adhoc:<n>); `answer` names a catalog answer card. A driver or vehicle item names its record. */
export interface AuditAddItemPayload {
  title: string
  kind: 'document' | 'answer'
  scope: AuditItemScope
  topic: AuditTopic
  card_code?: string | null
  subject_id?: string | null
  due_offset_hours?: number | null
}
export interface AuditAddItemResult { id: string; item_key: string }
export interface AuditItemSubject { id: string; label: string; status?: string | null }
/** GET /ops/audits/events/{id}/item-options: exactly what the add route accepts for this event. */
export interface AuditItemOptions {
  kinds: AuditAddItemPayload['kind'][]
  scopes: AuditItemScope[]
  topics: AuditTopic[]
  answer_cards: { code: string; title: string; topic: AuditTopic }[]
  drivers: AuditItemSubject[]
  vehicles: AuditItemSubject[]
}
export interface AuditHistoryRow {
  id: string
  event_type: string
  created_at: string
  message?: string
  client_visible?: boolean
}
export interface ClientEventView {
  id: string
  kind: AuditKind
  kind_label: string
  status: AuditEventStatus
  due_at: string | null
  due_in_hours: number | null
  due_band: DueBand
  counts: AuditCounts
  consultant_name?: string | null
  auditor_name?: string | null
  items?: ClientItem[]
  history?: AuditHistoryRow[]
}
export interface OpsEventView extends Omit<ClientEventView, 'items'> {
  items?: OpsItem[]
  client_name?: string | null
  prospect_name?: string | null
  owner_name?: string | null
  consultant_user_id?: string | null
  silent?: boolean
  channel?: string | null
  letter_date?: string | null
  received_at?: string | null
  last_activity_at?: string | null
  outcome?: string | null
  submission_code?: string | null
  notes?: string | null
}
export interface AuditList<T> { total: number; items: T[]; consultant_name?: string | null }
export interface AuditDisabled { kind: 'disabled'; code: 'audit_center_disabled' }
export interface AuditOk<T> { kind: 'ok'; data: T }
export type AuditResult<T> = AuditOk<T> | AuditDisabled

/** The pilot gate uses 404; other 404s remain errors (including inactive tokens). */
export function isAuditDisabled(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string; detail?: { code?: string } } } })?.response
  return response?.status === 404 &&
    (response.data?.code === 'audit_center_disabled' || response.data?.detail?.code === 'audit_center_disabled')
}

async function result<T>(request: Promise<{ data: T }>): Promise<AuditResult<T>> {
  try { return { kind: 'ok', data: (await request).data } }
  catch (error) {
    if (isAuditDisabled(error)) return { kind: 'disabled', code: 'audit_center_disabled' }
    throw error
  }
}

/** Client display rule only. Keep the wire value intact for staff and counts. */
export function clientItemStatus(status: AuditItemStatus, waived = false): AuditItemStatus {
  if (waived) return 'waived'
  return status === 'blocked_by_switch' || status === 'unavailable' ? 'open' : status
}

const CLIENT = '/v1/client/audits'
const SHARE = '/v1/share'
const OPS = '/v1/ops/audits'
const itemPath = (eventId: string, itemId: string) => `${CLIENT}/${encodeURIComponent(eventId)}/items/${encodeURIComponent(itemId)}`
const tokenItemPath = (token: string, itemId: string) => `${SHARE}/${encodeURIComponent(token)}/items/${encodeURIComponent(itemId)}`
const opsEventPath = (id: string) => `${OPS}/events/${encodeURIComponent(id)}`
export interface AuditUploadDates { inspection_date?: string; expiration_date?: string }
const form = (file: File, dates?: AuditUploadDates) => {
  const data = new FormData()
  data.append('file', file)
  if (dates?.inspection_date) data.append('inspection_date', dates.inspection_date)
  if (dates?.expiration_date) data.append('expiration_date', dates.expiration_date)
  return data
}
/** The api client defaults to JSON, and axios then serializes a FormData body to JSON, dropping the
 * file (422 on the real wire; the mock never saw it). Multipart lets axios set the boundary. */
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } }
const inlineError = { suppressErrorToast: true }
const inlineMultipart = { ...multipart, ...inlineError }
type Transport = {
  get: <T>(url: string, config?: { params?: Record<string, string | number | boolean | undefined>; suppressErrorToast?: boolean }) => Promise<{ data: T }>
  post: <T>(url: string, data?: unknown, config?: { headers?: Record<string, string>; suppressErrorToast?: boolean }) => Promise<{ data: T }>
  patch: <T>(url: string, data?: unknown) => Promise<{ data: T }>
  delete: <T>(url: string) => Promise<{ data: T }>
}
const transport: Transport = import.meta.env.CLAIMSHIELD_AUDIT_CENTER_MOCK === '1' ? auditCenterMockTransport : api
/** The token in the URL is the only credential a share call may carry (QA S26-C10). The shared api client
 * adds the signed-in user's Bearer, or in portal identity mode their session cookie and CSRF token, to every
 * request, so a staff or client browser leaked its own session to /v1/share (demo walk, 2026-11-02). This
 * client has no interceptors, and the fetch adapter with credentials omitted keeps the browser from
 * attaching cookies even to the same-origin /api. The base URL follows the shared client's choice. */
const shareClient = axios.create({ adapter: 'fetch', withCredentials: false, withXSRFToken: false, timeout: 30000 })
shareClient.interceptors.request.use((config) => {
  config.baseURL = isPortalIdentityMode() ? '/api' : api.defaults.baseURL
  return config
})
const shareTransport: Transport = import.meta.env.CLAIMSHIELD_AUDIT_CENTER_MOCK === '1' ? auditCenterMockTransport : shareClient
type PageParams = { limit?: number; offset?: number }
const kindLabels: Record<AuditKind, string> = {
  new_entrant: 'New entrant audit',
  compliance_review: 'Compliance review',
  rating_upgrade: 'Safety Management Plan',
  mock_audit: 'Mock audit',
}
const withKindLabel = <T extends ClientEventView>(event: T): T => ({ ...event, kind_label: event.kind_label || kindLabels[event.kind] })
async function eventResult<T extends ClientEventView>(request: Promise<{ data: T }>): Promise<AuditResult<T>> {
  const response = await result(request)
  return response.kind === 'ok' ? { kind: 'ok', data: withKindLabel(response.data) } : response
}
async function listResult<T extends ClientEventView>(request: Promise<{ data: AuditList<T> }>): Promise<AuditResult<AuditList<T>>> {
  const response = await result(request)
  return response.kind === 'ok' ? { kind: 'ok', data: { ...response.data, items: response.data.items.map(withKindLabel) } } : response
}
export const isAuditNotBuilt = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: string; detail?: { code?: string } } } })?.response
  return response?.status === 501 && (response.data?.code === 'not_built_yet' || response.data?.detail?.code === 'not_built_yet')
}

export const auditCenterClientService = {
  list: (params?: PageParams) => listResult(transport.get<AuditList<ClientEventView>>(CLIENT, { ...inlineError, ...(params ? { params } : {}) })),
  get: (id: string) => eventResult(transport.get<ClientEventView>(`${CLIENT}/${encodeURIComponent(id)}`, inlineError)),
  upload: (id: string, itemId: string, file: File, dates?: AuditUploadDates) => result(transport.post<ClientItem>(`${itemPath(id, itemId)}/upload`, form(file, dates), inlineMultipart)),
  answer: (id: string, itemId: string, answer: ClientItem['answer']) => result(transport.post<ClientItem>(`${itemPath(id, itemId)}/answer`, { answer }, inlineError)),
  history: (id: string, params?: PageParams) => result(transport.get<AuditList<AuditHistoryRow>>(`${CLIENT}/${encodeURIComponent(id)}/history`, params ? { params } : undefined)),
}

export const auditCenterTokenService = {
  getTokenEvent: (token: string) => eventResult(shareTransport.get<ClientEventView>(`${SHARE}/${encodeURIComponent(token)}/event`, inlineError)),
  upload: (token: string, itemId: string, file: File, dates?: AuditUploadDates) => result(shareTransport.post<ClientItem>(`${tokenItemPath(token, itemId)}/upload`, form(file, dates), inlineMultipart)),
  answer: (token: string, itemId: string, answer: ClientItem['answer']) => result(shareTransport.post<ClientItem>(`${tokenItemPath(token, itemId)}/answer`, { answer }, inlineError)),
  history: (token: string, params?: PageParams) => result(shareTransport.get<AuditList<AuditHistoryRow>>(`${SHARE}/${encodeURIComponent(token)}/history`, params ? { params } : undefined)),
}

export const auditCenterOpsService = {
  stats: () => result(transport.get<{ by_status: Record<string, number>; total: number }>(`${OPS}/stats`)),
  events: (params?: Record<string, string | number | boolean | undefined>) => listResult(transport.get<AuditList<OpsEventView>>(`${OPS}/events`, { params })),
  letters: (file: File) => result(transport.post<{ letter_key: string; proposal: Record<string, unknown> & { read_error?: string | null }; candidate_client: unknown }>(`${OPS}/letters`, form(file), multipart)),
  prospectLetter: (prospectId: string) => result(transport.post<{ letter_key: string; proposal: Record<string, unknown> & { read_error?: string | null }; candidate_client: unknown }>(`${OPS}/prospects/${encodeURIComponent(prospectId)}/letter`, {})),
  create: (payload: Record<string, unknown>) => eventResult(transport.post<OpsEventView>(`${OPS}/events`, payload)),
  get: (id: string) => eventResult(transport.get<OpsEventView>(opsEventPath(id))),
  patch: (id: string, payload: Record<string, unknown>) => eventResult(transport.patch<OpsEventView>(opsEventPath(id), payload)),
  items: (id: string, payload: AuditAddItemPayload) => result(transport.post<AuditAddItemResult>(`${opsEventPath(id)}/items`, payload, inlineError)),
  itemOptions: (id: string) => result(transport.get<AuditItemOptions>(`${opsEventPath(id)}/item-options`, inlineError)),
  pack: (id: string) => result(transport.post<Record<string, unknown>>(`${opsEventPath(id)}/pack`, {})),
  submissions: (id: string, params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${opsEventPath(id)}/submissions`, params ? { params } : undefined)),
  submit: (id: string, payload: Record<string, unknown>) => result(transport.post<Record<string, unknown>>(`${opsEventPath(id)}/submit`, payload)),
  acknowledge: (id: string, payload: Record<string, unknown> = {}) => eventResult(transport.post<OpsEventView>(`${opsEventPath(id)}/acknowledge`, payload)),
  outcome: (id: string, payload: Record<string, unknown>) => eventResult(transport.post<OpsEventView>(`${opsEventPath(id)}/outcome`, payload)),
  tokens: (id: string, payload: Record<string, unknown>) => result(transport.post<{ id: string; token: string; expires_at: string | null }>(`${opsEventPath(id)}/tokens`, payload)),
  revokeToken: (id: string, tokenId: string) => result(transport.delete<void>(`${opsEventPath(id)}/tokens/${encodeURIComponent(tokenId)}`)),
  watchlist: (params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${OPS}/watchlist`, params ? { params } : undefined)),
  readinessWorst: (params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${OPS}/readiness-worst`, params ? { params } : undefined)),
  ratingChanges: (params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${OPS}/rating-changes`, params ? { params } : undefined)),
  confirmRatingChange: (changeId: string, payload: Record<string, unknown>) => result(transport.post<OpsEventView | null>(`${OPS}/rating-changes/${encodeURIComponent(changeId)}/confirm`, payload)),
  vehicleAudit: (id: string) => result(transport.get<unknown>(`${OPS}/vehicles/${encodeURIComponent(id)}`)),
  vehicleReview: (params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${OPS}/vehicles/review`, params ? { params } : undefined)),
  reviewVehicleFile: (verificationId: string, action: 'accept' | 'reclassify', payload: Record<string, unknown>) =>
    result(transport.post<unknown>(`${OPS}/vehicles/review/${encodeURIComponent(verificationId)}/${action}`, payload)),
  itemAction: (eventId: string, itemId: string, action: 'waive' | 'request' | 'resolve' | 'accept', payload: Record<string, unknown>) =>
    result(transport.post<OpsItem>(`${opsEventPath(eventId)}/items/${encodeURIComponent(itemId)}/${action}`, payload)),
  /** The desk answers a paid prospect's row; a 422 `invalid_answer` is shown by the answer box, not a toast. */
  answer: (eventId: string, itemId: string, answer: ClientItem['answer']) =>
    result(transport.post<{ id: string; status: 'answered' }>(`${opsEventPath(eventId)}/items/${encodeURIComponent(itemId)}/answer`, { answer }, inlineError)),
  log: (id: string, params?: PageParams) => result(transport.get<AuditList<AuditHistoryRow>>(`${opsEventPath(id)}/log`, params ? { params } : undefined)),
  artifacts: (id: string, params?: PageParams) => result(transport.get<AuditList<Record<string, unknown>>>(`${opsEventPath(id)}/artifacts`, params ? { params } : undefined)),
  artifact: (id: string, kind: string) => result(transport.get<unknown>(`${opsEventPath(id)}/artifacts/${encodeURIComponent(kind)}`)),
  generateArtifact: (id: string, kind: string) => result(transport.post<unknown>(`${opsEventPath(id)}/artifacts/${encodeURIComponent(kind)}`, {})),
  generateDefaultArtifact: (id: string, kind: string) => result(transport.post<unknown>(`${opsEventPath(id)}/artifacts`, { kind })),
  // The paid door's desk steps (W2, 2026-11-03). The step calls keep errors inline: the screen
  // turns each documented 409/410/502 into a plain-words state, so a toast would say it twice.
  listProspects: (params?: PageParams & { status?: AuditProspectStatus }) => result(transport.get<AuditList<AuditProspectRow>>(`${OPS}/prospects`, params ? { params } : undefined)),
  getProspect: (id: string) => result(transport.get<AuditProspectDetail>(prospectPath(id), inlineError)),
  createZohoCustomer: (id: string, body: { confirm_existing_customer_id?: string } = {}) =>
    result(transport.post<AuditZohoCustomerResult>(`${prospectPath(id)}/zoho-customer`, body, inlineError)),
  issueInvoice: (id: string) => result(transport.post<{ invoice_url: string }>(`${prospectPath(id)}/invoice`, {}, inlineError)),
  convertProspect: (id: string, clientId: string) =>
    result(transport.post<{ prospect_id: string; converted_client_id: string; retention_until: string | null }>(`${prospectPath(id)}/convert`, { client_id: clientId }, inlineError)),
  staffIntake: (payload: AuditStaffIntakePayload) =>
    result(transport.post<{ prospect_id: string; status: string }>(`${OPS}/prospects/intake`, intakeForm(payload), inlineMultipart)),
}

/** Where a paid-door prospect stands; the server owns it. Precedence: purged, converted, opened, paid, invoiced, pending_customer. */
export type AuditProspectStatus = 'pending_customer' | 'invoiced' | 'paid' | 'opened' | 'converted' | 'purged'
/** The paid-door row (W1 contract, lab/audit_center/2026-11-03-w1-intake/CONTRACT.md). A purged row
 * has null contact, company, billing ids and payment link. */
export interface AuditProspectRow {
  id: string
  company_name: string | null
  dot_number: string | null
  contact_name: string | null
  email: string | null
  status: AuditProspectStatus
  created_at: string
  zoho_customer_id: string | null
  zoho_invoice_id: string | null
  invoice_state: 'not_issued' | 'issued' | 'purged'
  payment_state: 'unpaid' | 'paid' | 'purged'
  payment_link: string | null
  event_id: string | null
  purged_at: string | null
  retention_until: string | null
}
/** What the Zoho customer step will send, shown for a person to read before pressing the button. */
export interface AuditZohoCustomerDraft {
  display_name: string
  company_name: string
  dot_number: string | null
  contact_name: string
  email: string
  phone: string | null
}
export interface AuditProspectDetail extends AuditProspectRow {
  letter_proposal: Record<string, unknown> | null
  draft_zoho_customer: AuditZohoCustomerDraft | null
}
export interface AuditZohoCustomerResult { zoho_customer_id: string; created: boolean }
/** The desk keys in a carrier who sent the letter by phone or email. `data_notice_ack` is the desk
 * confirming it told the carrier how their letter and details are kept; the route requires it. */
export type AuditStaffIntakePayload = AuditIntakePayload

/** Each documented failure of a paid-door step, read from FastAPI's `detail`. */
export type AuditProspectFailure =
  | { code: 'zoho_customer_exists'; customer_id: string; display_name: string | null }
  | { code: 'zoho_customer_confirmation_stale' | 'prospect_purged' | 'zoho_unavailable' | 'needs_zoho_customer'
    | 'prospect_already_linked' | 'client_not_found' | 'invoice_email_failed' | 'invoice_email_rate_limited' | 'not_found' }
  | { code: 'unknown'; status: number | null; message: string }
const knownProspectCodes = new Set(['zoho_customer_confirmation_stale', 'prospect_purged', 'zoho_unavailable', 'needs_zoho_customer',
  'prospect_already_linked', 'client_not_found', 'invoice_email_failed', 'invoice_email_rate_limited', 'not_found'])
export function prospectFailure(error: unknown): AuditProspectFailure {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown; code?: unknown } } })?.response
  const raw = response?.data?.detail ?? response?.data
  const detail = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {}
  const code = typeof detail.code === 'string' ? detail.code : ''
  if (code === 'zoho_customer_exists' && typeof detail.customer_id === 'string' && detail.customer_id) {
    return { code, customer_id: detail.customer_id, display_name: typeof detail.display_name === 'string' ? detail.display_name : null }
  }
  if (knownProspectCodes.has(code)) return { code } as AuditProspectFailure
  return { code: 'unknown', status: response?.status ?? null, message: error instanceof Error ? error.message : '' }
}

const prospectPath = (id: string) => `${OPS}/prospects/${encodeURIComponent(id)}`
function intakeForm(payload: AuditStaffIntakePayload): FormData {
  const data = new FormData()
  data.append('company_name', payload.company_name)
  if (payload.dot_number) data.append('dot_number', payload.dot_number)
  data.append('contact_name', payload.contact_name)
  data.append('email', payload.email)
  if (payload.phone) data.append('phone', payload.phone)
  data.append('product', payload.product)
  data.append('data_notice_ack', String(payload.data_notice_ack))
  data.append('letter', payload.letter)
  return data
}

export const auditCenterPublicService = {
  intake: (payload: AuditIntakePayload) => {
    const data = new FormData()
    data.append('company_name', payload.company_name)
    if (payload.dot_number) data.append('dot_number', payload.dot_number)
    data.append('contact_name', payload.contact_name)
    data.append('email', payload.email)
    if (payload.phone) data.append('phone', payload.phone)
    data.append('product', payload.product)
    data.append('data_notice_ack', String(payload.data_notice_ack))
    data.append('letter', payload.letter)
    return result(transport.post<{ prospect_id: string; status: 'pending_customer' }>('/v1/public/audit-intake', data, multipart))
  },
}
